// ANA-R3-GEOM-04 — edit an existing defect's geometry in place.
//
// Sibling of GEOM-03's DrawOverlay; it reuses the SAME seams:
//   - the OSD `ViewerViewportApi` published into `viewportApiStore` →
//     a `CoordinateTransform` so the editor re-projects on pan/zoom;
//   - the shared `PolygonEditor` primitive (GEOM-02) for move/add/remove
//     vertices + constrained rect-resize;
//   - the gesture-forwarding pattern (wheel + secondary/middle mouse →
//     the Viewer wrapper) so OSD zoom/pan stays live mid-edit.
//
// The editor is seeded from the persisted defect's NORMALISED geometry (held in
// `editGeometryStore`, mode chosen by `detectMode`; legacy null-path rows seed
// from the 4 bbox corners — AC4). Edits are a DRAFT layered over the persisted
// shape: this overlay paints the draft, and AnalysisFeature hides the original
// outline of the defect under edit. An explicit **Save** serialises the draft to
// a PIXEL path and PATCHes it; **Cancel/Esc** discards the draft and restores the
// persisted shape (AC2). Nothing persists without Save.
//
// Undo (AC3) is routed to the primitive's own `api.undo()` — the host installs a
// per-image-stack trigger via the toolbar; we do NOT keep a parallel geometry
// stack here.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  PolygonEditor,
  type CoordinateTransform,
  type EditorMode,
  type Point,
  type PolygonEditorApi,
  type PolygonValue,
} from "@aeriosiq/ui";
import { useEditGeometryStore } from "../state/editGeometryStore.js";
import { useViewportApiStore } from "../state/viewportApiStore.js";
import { useEscapeLayer } from "../state/useEscapeLayer.js";
import { ESCAPE_PRIORITY } from "../state/escapeStack.js";

const VIEWER_WRAPPER_SEL = '[data-testid="viewer-wrapper"]';
const VIEWER_CELL_SEL = "[data-active-image-id]";

export interface EditGeometryOverlayProps {
  /**
   * Receives the live PolygonEditor API so the host toolbar can drive Save
   * (commit) + Undo from its own controls / the per-image undo stack. Receives
   * `null` on unmount.
   */
  onEditorApi?: (api: PolygonEditorApi | null) => void;
}

export function EditGeometryOverlay({
  onEditorApi,
}: EditGeometryOverlayProps): React.ReactElement | null {
  const defectId = useEditGeometryStore((s) => s.defectId);
  const mode = useEditGeometryStore((s) => s.mode);
  const points = useEditGeometryStore((s) => s.points);
  const setDraft = useEditGeometryStore((s) => s.setDraft);
  const cancel = useEditGeometryStore((s) => s.cancel);
  const viewportApi = useViewportApiStore((s) => s.api);

  const editing = defectId !== null;

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const apiRef = useRef<PolygonEditorApi | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });

  // Track the wrapper size so the editor's Konva Stage matches the overlay.
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    const measure = () => {
      const r = node.getBoundingClientRect();
      setSize({ width: r.width, height: r.height });
    };
    measure();
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    ro?.observe(node);
    return () => {
      ro?.disconnect();
    };
  }, [editing]);

  // Build a CoordinateTransform over the published OSD viewport API. Falls back
  // to a linear surface transform (tests / pre-open) so the editor still works.
  const transform = useMemo<CoordinateTransform>(() => {
    if (viewportApi) {
      return {
        imageToScreen: (p: Point) => viewportApi.imageToScreen(p),
        screenToImage: (p) => viewportApi.screenToImage(p),
        version: viewportApi.version,
      };
    }
    const w = size.width || 1;
    const h = size.height || 1;
    return {
      imageToScreen: (p: Point) => ({ x: p.x * w, y: p.y * h }),
      screenToImage: (p) => ({ x: p.x / w, y: p.y / h }),
      version: w * 100000 + h,
    };
  }, [viewportApi, size.width, size.height]);

  const value: PolygonValue = useMemo(() => ({ mode, points }), [mode, points]);

  // Every editor change (drag, insert, delete, convert, undo) writes the draft
  // back to the store so the overlay re-renders + the toolbar can read it.
  const handleChange = useCallback(
    (next: PolygonValue) => {
      setDraft(next.mode, next.points);
    },
    [setDraft],
  );

  // Resolve the sibling Viewer wrapper (for forwarding gestures to OSD).
  const findViewerWrapper = useCallback((): HTMLElement | null => {
    const overlay = wrapperRef.current;
    if (!overlay) return null;
    return (
      overlay
        .closest(VIEWER_CELL_SEL)
        ?.querySelector<HTMLElement>(VIEWER_WRAPPER_SEL) ?? null
    );
  }, []);

  // ANA-R3-ESC-01 — edit layer. Esc cancels/discards the edit draft (AC2). It
  // sits at the draw priority so it pre-empts deselect (the defect stays
  // selected after the edit is cancelled — the user only exits edit mode).
  useEscapeLayer({
    id: "analysis:edit-geometry",
    priority: ESCAPE_PRIORITY.draw,
    isActive: editing,
    onEscape: () => {
      cancel();
    },
  });

  // Forward wheel to the Viewer's wrapper so OSD's listener drives zoom.
  const forwardWheel = useCallback(
    (e: WheelEvent): void => {
      const viewerWrapper = findViewerWrapper();
      if (!viewerWrapper) return;
      e.preventDefault();
      viewerWrapper.dispatchEvent(
        new WheelEvent(e.type, {
          deltaX: e.deltaX,
          deltaY: e.deltaY,
          deltaMode: e.deltaMode,
          clientX: e.clientX,
          clientY: e.clientY,
          ctrlKey: e.ctrlKey,
          metaKey: e.metaKey,
          bubbles: true,
          cancelable: true,
        }),
      );
    },
    [findViewerWrapper],
  );

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || !editing) return;
    node.addEventListener("wheel", forwardWheel, { passive: false });
    return () => {
      node.removeEventListener("wheel", forwardWheel);
    };
  }, [editing, forwardWheel]);

  // Secondary / middle mouse → forward to OSD for marquee-zoom / pan.
  const handleMouseDownCapture = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      if (e.button === 0) return; // left = vertex edit via the editor
      const viewerWrapper = findViewerWrapper();
      if (!viewerWrapper) return;
      viewerWrapper.dispatchEvent(
        new MouseEvent("mousedown", {
          button: e.button,
          buttons: e.buttons,
          clientX: e.clientX,
          clientY: e.clientY,
          bubbles: true,
          cancelable: true,
        }),
      );
    },
    [findViewerWrapper],
  );

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      e.preventDefault();
    },
    [],
  );

  if (!editing) return null;

  return (
    <Box
      ref={wrapperRef}
      data-testid="edit-geometry-overlay"
      data-edit-mode={mode}
      role="presentation"
      onMouseDownCapture={handleMouseDownCapture}
      onContextMenu={handleContextMenu}
      sx={{
        position: "absolute",
        inset: 0,
        // Above the OSD canvas + Konva read overlay, below the ViewerToolbar
        // (z12) so its Save/Cancel buttons stay clickable mid-edit.
        zIndex: 11,
        cursor: "crosshair",
      }}
    >
      {size.width > 0 && size.height > 0 ? (
        <PolygonEditor
          value={value}
          onChange={handleChange}
          transform={transform}
          width={size.width}
          height={size.height}
          editable
          ariaLabel={`Edit defect geometry (${mode})`}
          apiRef={(api) => {
            apiRef.current = api;
            onEditorApi?.(api);
          }}
        />
      ) : null}
    </Box>
  );
}

export type { EditorMode };
